import { useEffect, useState, useCallback } from 'react';
import {
  IAssignTaskParams,
  IAssignTaskToStaffParams,
  IParamsUpdateTask,
  ITaskParams,
  ITicket,
  IParamsUpdateTaskAddProfileMC,
} from '../interfaces/task';
import { IPagination } from '../interfaces';
import { commonConstants, taskConstant } from 'constants/index';
import { arrayHelpers } from '../helpers';
import { Form } from 'antd';
import pickBy from 'lodash/pickBy';
import { ITaskDetail } from 'interfaces';
import { fileService, taskServices } from 'services';
import { browserHistory } from 'helpers';
import { IUpdateSposContractParams } from '../interfaces/sposContract';

const { DEFAULT_SIZE_CHANGER } = commonConstants;
const { URL_MINIO_PUBLIC } = taskConstant;

const useTasks = (defaultParams: ITaskParams) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [params, setParams] = useState<ITaskParams>({
    ...defaultParams,
  });
  const [tickets, setTickets] = useState<Array<ITicket>>([]);

  const [pagination, setPagination] = useState<IPagination>({
    ...DEFAULT_SIZE_CHANGER,
  });

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const result = await taskServices.getTasks(params);

      const { data, pagination } = result;
      // set data tickets for showing on the table
      setTickets(data);

      let pager = { ...pagination };
      // set metadata to pagination
      pager = arrayHelpers.mapPagerApiToAntdPager(pagination, pager);
      setPagination(pager);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: IPagination) => {
    const newQueryParams = {
      ...params,
      page: pagination.current,
      page_size: pagination.pageSize,
    };
    // @ts-ignore
    setParams(newQueryParams);
  };

  const onFinish = (values: any) => {
    const data = {
      ...pickBy(values, (e: any) => {
        return e !== '' && e !== undefined && e !== null;
      }),
    };
    const newParams = { ...params, ...data };
    const newQuery = {
      ...newParams,
    };

    setParams(newQuery);
  };

  const doUpdateStatus = async (taskId: number, isActive: boolean) => {
    try {
      setIsUpdating(true);
      await taskServices.updateTaskStatus(taskId, isActive).then(() => {
        setTimeout(() => {
          fetchTasks();
        }, 1000);
      });
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    fetchTasks().then();
  }, [params]);

  return {
    loading,
    setLoading,
    params,
    setParams,
    tickets,
    form,
    pagination,
    onFinish,
    handleTableChange,
    isUpdating,
    doUpdateStatus,
  };
};

const useTask = () => {
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [task, setTask] = useState<ITaskDetail>();
  const doCreateTask = async (values: any) => {
    try {
      setIsCreating(true);
      values.issue_type_group_id = window.SALE_PORTAL_ISSUE_TYPE_GROUP;
      values.department_processing_id = window.SALE_PORTAL_ISSUE_TYPE_GROUP;
      values.priority_id = window.SALE_PORTAL_PRIORITY_ID;
      values.ticket_type = values.task_name;
      await taskServices.createTask(values).then(() => {
        setTimeout(() => {
          setIsCreating(false);
          window.location.href = '/implement/tasks';
        }, 2000);
      });
    } finally {
      setIsCreating(false);
    }
  };

  const doUpdateTask = async (values: any) => {
    try {
      setIsCreating(true);
      // values.issue_type_group_id = window.SALE_PORTAL_ISSUE_TYPE_GROUP;
      // values.department_processing_id = window.SALE_PORTAL_ISSUE_TYPE_GROUP;
      // values.priority_id = window.SALE_PORTAL_PRIORITY_ID;

      values.ticket_type = values.task_name;
      await taskServices.updateTask(values).then(() => {
        setTimeout(() => {
          setIsCreating(false);
          window.location.href = '/implement/tasks';
        }, 2000);
      });
    } finally {
      setIsCreating(false);
    }
  };

  const getTask = useCallback(async (id: string) => {
    try {
      const res = await taskServices.getTask(id);
      setTask(res.data);
    } catch (error) {
      browserHistory.push('/404');
    }
  }, []);

  const updateTask = async (id: string, params: IParamsUpdateTask) => {
    const res = await taskServices.updateTaskDetail(id, params);
    return res;
  };

  const updateTaskAddProfileMC = async (
    params: IParamsUpdateTaskAddProfileMC
  ) => {
    const res = await taskServices.updateTaskAddProfileMC(params);
    return res;
  };

  const uploadImages = async (files: any) => {
    return new Promise(async (resolve, reject) => {
      let images = [] as IParamsUpdateTask['internalProcessFiles'];

      for (let idx = 0; idx < files.length; idx++) {
        const file = files[idx];
        try {
          if (file) {
            let res = await fileService.uploadFile(file);
            const image = {
              fileName: res.data.fileName,
              url: URL_MINIO_PUBLIC + res.data.fileName,
            };
            images.push(image);
          }
        } catch (error) {
          reject();
        }
      }
      resolve(images);
    });
  };

  const updateSposContract = async (params: IUpdateSposContractParams) => {
    const data = {
      ...pickBy(params, (e: any) => {
        return e !== '' && e !== undefined && e !== null;
      }),
    };
    const res = await taskServices.updateSposContract(
      data as IUpdateSposContractParams
    );
    return res;
  };

  return {
    isCreating,
    doCreateTask,
    doUpdateTask,
    getTask,
    task,
    updateTask,
    uploadImages,
    updateTaskAddProfileMC,
    updateSposContract,
  };
};

const useAssignTask = () => {
  const assignTask = (params: IAssignTaskParams) => {
    return taskServices.assignTask(params);
  };

  const assignTaskToStaff = (params: IAssignTaskToStaffParams) => {
    return taskServices.assignTasktoStaff(params);
  };

  return { assignTask, assignTaskToStaff };
};

export default {
  useTasks,
  useTask,
  useAssignTask,
};
